// @noflow

import React, { useMemo, useState, useEffect } from "react";
import { Modal, Tabs, Tab } from "react-bootstrap";

import { classNames, niceDate, dayString } from "../../lib/Util";
import { arePeopleSchedulesEqual } from "../../lib/Shifts";
import { translate } from "../IntlProviderWrapper";
import ErrorComponent from "../ErrorComponent";
import Loader from "../Loader";
import MultiSelect from "../MultiSelect";

import type { Row, Column } from "react-table";
import ReactTable, { DateFilter, type DateFilterProps, type ReactTableColumn } from "../react/ReactTable";

import {
    useWorkerExchangeData,
    getExchangeAvailabilities,
    buildExchangeListingLookup,
    addExchangeListing,
    deleteExchangeListing,
    cancelExchangeListing,
    acceptExchangeRequest,
    rejectExchangeRequest,
    createExchangeRequest,
    cancelExchangeRequest,
    buildMyOfferingsRows,
    buildMyAvailabilitiesRows,
    buildOtherOfferingsRows,
    buildMyBookingsRows,
    getListingKey,
    getRequestConfirmKey,
    getRequestRejectKey,
    getUniqueShiftValues,
    getUniqueCompetenceCodes,
    getUniqueValues,
    getAvailabilityKey,
    getStatusLabel,
    WORKER_EXCHANGE_VIEWS,
    WORKER_EXCHANGE_TABS,
    getTabFromView,
    getDefaultView,
    type Availability,
    type WorkerExchangeView,
    type WorkerExchangeTab,
    type MyOfferingsRow,
    type AvailabilityRow,
    type OtherOfferingsRow,
    type MyBookingsRow,
    ExchangeListingStatus,
} from "../../lib/workerExchange";
import { SHIFT_EXCHANGE_LISTING_STATUS, SHIFT_EXCHANGE_REQUEST_STATUS } from "../../lib/ManufacturingConsts.generated";

import type {
    ICompetenceData,
    IPersonData,
    IShiftPersonLineRec,
    IShiftExchangeListing,
    IShiftExchangeRequest,
    ILineGroupData,
} from "../../lib/backend/manufacturing2.generated.types";
import type { ShiftExchangeRequestStatusEnum } from "../../lib/ManufacturingConsts.generated";

import "./WorkerExchange.scss";

// ---------------------------------------------------------------------------------------------------------------------
// Filter functions
// ---------------------------------------------------------------------------------------------------------------------

const filterEqualDay = (rows: Row[], id: string[], filterValue: string): Row[] =>
    rows.filter(row => {
        const rowDate = new Date(Number(row.values[id[0]]));
        const filterDate = new Date(Number(filterValue));
        // Normalize the times to midnight
        rowDate.setHours(0, 0, 0, 0);
        filterDate.setHours(0, 0, 0, 0);
        return rowDate.getTime() === filterDate.getTime();
    });

const filterShiftMultiSelect = (rows: Row[], id: string[], filterValues: string[]): Row[] => {
    if (filterValues.length === 0) {
        return rows;
    }
    // Convert "1", "2", "3" to 0, 1, 2 for comparison
    const selectedShiftNumbers = filterValues.map(valueString => parseInt(valueString) - 1);
    return rows.filter(row => {
        const rowShiftNumber = row.values[id[0]] as number;
        return selectedShiftNumbers.includes(rowShiftNumber);
    });
};

const filterCompetencesMultiSelect = (rows: Row[], id: string[], filterValues: string[]): Row[] => {
    if (filterValues.length === 0) {
        return rows;
    }
    return rows.filter(row => {
        const rowCompetences = row.values[id[0]] as ICompetenceData[];
        const rowCompetenceCodes = rowCompetences.map(c => c.code).filter(Boolean);
        if (rowCompetenceCodes.length === 0) {
            rowCompetenceCodes.push(`(${translate("WorkerExchange.noCompetences", "No competences")})`);
        }
        // Check if any of the selected competence codes match any of the row's competence codes
        return filterValues.some(selectedCode => rowCompetenceCodes.includes(selectedCode));
    });
};

const filterMultiSelect = (rows: Row[], id: string[], filterValues: string[]): Row[] => {
    if (filterValues.length === 0) {
        return rows;
    }
    return rows.filter(row => {
        const value = row.values[id[0]] as string;
        return filterValues.includes(value);
    });
};

// ---------------------------------------------------------------------------------------------------------------------
// Sort functions
// ---------------------------------------------------------------------------------------------------------------------

const sortNumber = (rowA: Row, rowB: Row, columnId: string): number => {
    return rowB.original[columnId] - rowA.original[columnId];
};

const sortString = (rowA: Row, rowB: Row, columnId: string): number => {
    const a = String(rowA.original[columnId]).toLowerCase();
    const b = String(rowB.original[columnId]).toLowerCase();
    return b.localeCompare(a);
};

const sortCompetences = (rowA: Row, rowB: Row, columnId: string): number => {
    const a = rowA.original[columnId]
        .map(competence => competence.code)
        .join(",")
        .toLowerCase();
    const b = rowB.original[columnId]
        .map(competence => competence.code)
        .join(",")
        .toLowerCase();
    return b.localeCompare(a);
};

// ---------------------------------------------------------------------------------------------------------------------
// WorkerExchangeModal
// ---------------------------------------------------------------------------------------------------------------------

const WORKER_EXCHANGE_MODAL_ID = "worker-exchange-modal";

type WorkerExchangeModalProps = {
    show: boolean;
    originalSchedule: IShiftPersonLineRec[];
    currentSchedule: IShiftPersonLineRec[];
    children: React.ReactNode;
    onSave: () => Promise<void>;
    onClose: () => void;
};

const WorkerExchangeModal = (props: WorkerExchangeModalProps) => {
    const { show, originalSchedule, currentSchedule, onSave, onClose, children } = props;
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [error, setError] = useState<string>("");

    const hasUnsavedChanges = useMemo(
        () => show && !arePeopleSchedulesEqual(currentSchedule, originalSchedule),
        [originalSchedule, currentSchedule, show]
    );

    const handleSave = async () => {
        setIsSaving(true);
        setError("");

        try {
            await onSave();
        } catch (error: unknown) {
            setError(translate("common.error_save", "An error occurred while saving data"));
            console.error("Failed to save schedule:", error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal
            id={WORKER_EXCHANGE_MODAL_ID}
            size={hasUnsavedChanges ? undefined : "lg"}
            className={classNames({ "worker-exchange-modal": !hasUnsavedChanges })}
            dialogClassName={classNames({ "modal-dialog-scrollable modal-max h-100": !hasUnsavedChanges })}
            show={show}
            onHide={onClose}
            animation={false}
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    {hasUnsavedChanges
                        ? translate("ShiftAssignmentWeekPool.unsaved_changes", "Unsaved changes")
                        : translate("WorkerExchange.workerExchange", "Worker Exchange")}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error !== "" && <ErrorComponent className="mt-0" type="error" msg={error} />}
                {hasUnsavedChanges ? (
                    <ErrorComponent
                        className="my-0"
                        type="warning"
                        msg={translate("Shifts.please_save", "The shift schedule has changed. Please save the data.")}
                    />
                ) : (
                    children
                )}
            </Modal.Body>
            {hasUnsavedChanges && (
                <Modal.Footer>
                    <button className="btn btn-outline-secondary" onClick={onClose}>
                        {translate("common.cancel", "Cancel")}
                    </button>
                    <button className="btn btn-primary" onClick={handleSave} autoFocus>
                        {translate("common.save", "Save")} {isSaving && <i className="fas fa-spinner fa-spin ml-1" />}
                    </button>
                </Modal.Footer>
            )}
        </Modal>
    );
};

// ---------------------------------------------------------------------------------------------------------------------
// Cell Components
// ---------------------------------------------------------------------------------------------------------------------

const CompetencesCell = ({ value }: { value: ICompetenceData[] }) => (
    <span>
        {value
            .map(c => c.code)
            .filter(Boolean)
            .join(", ") || "-"}
    </span>
);
const DateCell = ({ value }: { value: Date }) => (
    <span>
        {dayString((value.getDay() + 6) % 7)}, {niceDate(value)}
    </span>
);

const ShiftCell = ({ value }: { value: number }) => (
    <span className={`badge badge-shift badge-shift-${value + 1}`}>{value + 1}</span>
);

type FilterProps = {
    filterValue?: string[];
    values: string[];
    id: string;
    setFilter: (column_id: string, value?: string[]) => void;
    placeholder?: string;
    getLabel?: (value: string) => string;
};

const MultiSelectFilter = (props: FilterProps) => {
    const [selectedValues, setSelectedValues] = useState<string[]>(() => props.filterValue ?? []);

    // Sync with external filterValue changes
    useEffect(() => {
        setSelectedValues(props.filterValue ?? []);
    }, [props.filterValue]);

    // ["1", "2", "3"] -> [{ value: "1", label: "1" }, ...]
    const options = props.values.map((value: string) => ({
        value,
        label: props.getLabel ? props.getLabel(value) : value,
    }));

    const selectedOptions = selectedValues
        .map(value => options.find(opt => opt.value === value))
        .filter(Boolean) as Array<{ value: string; label: string }>;

    const onChange = (selectedOptions: Array<{ value: string; label: string }> | null) => {
        const newSelectedValues = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
        setSelectedValues(newSelectedValues);
        props.setFilter(props.id, newSelectedValues);
    };

    return (
        <MultiSelect
            options={options}
            value={selectedOptions}
            onChange={onChange}
            placeholder={props.placeholder || translate("common.filter", "Filter")}
            isClearable
            show_count={true}
            adaptSizeToPlaceholder={true}
            fullWidth={true}
        />
    );
};

const ShiftDateFilter = (props: DateFilterProps) => {
    return <DateFilter {...props} popupParent={document.getElementById(WORKER_EXCHANGE_MODAL_ID)?.parentElement} />;
};

const StatusCell = ({ value }: { value: string }) => {
    const label = getStatusLabel(value);
    return <span className={`badge badge-wide badge-status badge-${value}`}>{label}</span>;
};

const NameAndIdCell = ({ row }: { row: { original: { personName: string; personExternalId: string } } }) => (
    <span>
        {row.original.personName} ({row.original.personExternalId})
    </span>
);

const DestinationCell = ({ value }: { value: string }) => <span>{value || "-"}</span>;

const SourceCell = ({ value }: { value: string }) => <span>{value || "-"}</span>;

// ---------------------------------------------------------------------------------------------------------------------
// WorkerExchangeMyAvailabilities
// ---------------------------------------------------------------------------------------------------------------------

type AvailabilityActionsCellProps = {
    row: { original: AvailabilityRow };
    submittingIds: Set<string>;
    onOfferAdd: (personId: number, shiftLineId: number) => void;
};

const AvailabilityActionsCell = (props: AvailabilityActionsCellProps) => {
    const { row, submittingIds, onOfferAdd } = props;
    const { personId, shiftLineId } = row.original;
    const key = getAvailabilityKey(personId, shiftLineId);
    const isSubmitting = submittingIds.has(key);

    return (
        <button
            className="btn btn-sm btn-primary"
            onClick={() => onOfferAdd(personId, shiftLineId)}
            disabled={isSubmitting}
        >
            {isSubmitting && <i className="fas fa-spinner fa-spin mr-1" />}
            {translate("common.add", "Add")}
        </button>
    );
};

type WorkerExchangeMyAvailabilitiesProps = {
    className?: string;
    availabilities: Availability[];
    people: IPersonData[];
    competencesById: Map<number, ICompetenceData>;
    selectedPersonId: number | null;
    lineGroupId: number;
    isRefetching: boolean;
    onError: (error: string) => void;
    onOfferAdded: () => Promise<void>;
    onViewOfferings: () => void;
};

const WorkerExchangeMyAvailabilities = (props: WorkerExchangeMyAvailabilitiesProps) => {
    const {
        className,
        availabilities,
        competencesById,
        people,
        selectedPersonId,
        lineGroupId,
        isRefetching,
        onError,
        onViewOfferings,
        onOfferAdded,
    } = props;

    const [submittingShiftLineIds, setSubmittingShiftLineIds] = useState<Set<string>>(new Set());

    // Transform to table rows
    const tableData: AvailabilityRow[] = useMemo(
        () => buildMyAvailabilitiesRows(availabilities, competencesById),
        [availabilities, competencesById]
    );

    const uniqueShiftValues = useMemo(() => getUniqueShiftValues(tableData), [tableData]);
    const uniqueCompetenceCodes = useMemo(() => getUniqueCompetenceCodes(tableData), [tableData]);
    const uniquePersonNames = useMemo(() => getUniqueValues(tableData, "personName"), [tableData]);

    // Find the person name from selectedPersonId for initial filter
    const initialPersonName = useMemo(() => {
        if (selectedPersonId === null) {
            return null;
        }
        const selectedPerson = people.find(person => person.id === selectedPersonId);
        return selectedPerson?.name ?? null;
    }, [selectedPersonId, people]);

    const handleOfferAdd = async (personId: number, shiftLineId: number) => {
        const key = getAvailabilityKey(personId, shiftLineId);
        setSubmittingShiftLineIds(prev => {
            const newSet = new Set(prev);
            newSet.add(key);
            return newSet;
        });
        onError("");

        try {
            await addExchangeListing(lineGroupId, personId, [shiftLineId]);
            await onOfferAdded();
        } catch (err: unknown) {
            onError(translate("WorkerExchange.failedToCreateOffer", "Failed to create the offer"));
            console.error("Failed to create the offer:", err);
        } finally {
            setSubmittingShiftLineIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(key);
                return newSet;
            });
        }
    };

    const columns: ReactTableColumn<AvailabilityRow>[] = [
        {
            Header: "",
            accessor: "personName",
            enableSorting: true,
            sortType: sortString,
            filter: filterMultiSelect,
            Filter: (props: Column<AvailabilityRow>) => (
                <MultiSelectFilter
                    values={uniquePersonNames}
                    id={props.column.id}
                    setFilter={props.setFilter}
                    filterValue={props.column.filterValue}
                    placeholder={translate("common.people", "People")}
                />
            ),
        },
        {
            Header: "",
            accessor: "competences",
            Cell: CompetencesCell,
            enableSorting: true,
            sortType: sortCompetences,
            filter: filterCompetencesMultiSelect,
            Filter: (props: Column<AvailabilityRow>) => (
                <MultiSelectFilter
                    values={uniqueCompetenceCodes}
                    id={props.column.id}
                    setFilter={props.setFilter}
                    filterValue={props.column.filterValue}
                    placeholder={translate("common.competences", "Competences")}
                />
            ),
        },
        {
            Header: "",
            accessor: "date",
            Cell: DateCell,
            textAlign: "right",
            filter_type: "date",
            filter: filterEqualDay,
            filterInputPlaceholder: translate("common.date", "Date"),
            DateFilter: ShiftDateFilter,
            enableSorting: true,
            sortType: sortNumber,
        },
        {
            Header: "",
            accessor: "shiftNumber",
            Cell: ShiftCell,
            textAlign: "center",
            enableSorting: true,
            sortType: sortNumber,
            filter: filterShiftMultiSelect,
            Filter: (props: Column<AvailabilityRow>) => (
                <MultiSelectFilter
                    values={uniqueShiftValues}
                    id={props.column.id}
                    setFilter={props.setFilter}
                    filterValue={props.column.filterValue}
                    placeholder={translate("common.shifts", "Shifts")}
                />
            ),
        },
        {
            Header: translate("common.actions", "Actions"),
            accessor: "shiftLineId",
            disableFilters: true,
            Cell: ({ row }) => (
                <AvailabilityActionsCell row={row} submittingIds={submittingShiftLineIds} onOfferAdd={handleOfferAdd} />
            ),
            textAlign: "right",
        },
    ];

    // Build initialState for ReactTable to preselect the person filter
    const tableInitialState = useMemo(() => {
        if (initialPersonName === null) {
            return undefined;
        }

        return {
            filters: [
                {
                    id: "personName",
                    value: [initialPersonName],
                },
            ],
        };
    }, [initialPersonName]);

    const filteredTableData = useMemo(
        () => (selectedPersonId ? tableData.filter(row => row.personId === selectedPersonId) : []),
        [selectedPersonId, tableData]
    );

    const filteredColumns = useMemo(() => {
        return selectedPersonId
            ? columns.filter(column => column.accessor !== "personName" && column.accessor !== "competences")
            : columns;
    }, [selectedPersonId, columns]);

    const errorMessage =
        selectedPersonId && filteredTableData.length === 0
            ? translate(
                  "WorkerExchange.warning.noAvailabilitiesForPerson",
                  "No availabilities remain for the selected person"
              )
            : undefined;

    return (
        <>
            <WorkerExchangeSubHeader
                variant="offerings"
                title={
                    <>
                        {initialPersonName ? (
                            <i className="fas fa-user icon-smaller" />
                        ) : (
                            <i className="fas fa-plus icon-smaller" />
                        )}
                        {translate("WorkerExchange.addNewOfferings", "Add new offerings")}
                        <strong>{initialPersonName ? `(${initialPersonName})` : ""}</strong>
                        {isRefetching && <i className="fas fa-spinner fa-spin" />}
                    </>
                }
                menu={
                    <div className="d-flex gap-2">
                        <button className="btn btn-primary btn-outline-primary" onClick={onViewOfferings}>
                            <i className="fas fa-list mr-1" />
                            {translate("WorkerExchange.activeOfferings", "Active offerings")}
                        </button>
                    </div>
                }
            />
            <div className={classNames("worker-exchange-my-availabilities", className)}>
                <ReactTable
                    data={selectedPersonId ? filteredTableData : tableData}
                    columns={selectedPersonId ? filteredColumns : columns}
                    initialState={tableInitialState}
                    error_msg={errorMessage}
                />
            </div>
        </>
    );
};

// ---------------------------------------------------------------------------------------------------------------------
// WorkerExchangeMyOfferings
// ---------------------------------------------------------------------------------------------------------------------

type MyOfferingsActionsCellProps = {
    row: { original: MyOfferingsRow };
    submittingIds: Set<string>;
    onCancelListing: (listingId: number, status: ExchangeListingStatus) => void;
    onConfirmRequest: (requestId: number) => void;
    onRejectRequest: (requestId: number) => void;
};

const MyOfferingsActionsCell = (props: MyOfferingsActionsCellProps) => {
    const { row, submittingIds, onCancelListing, onConfirmRequest, onRejectRequest } = props;
    const { listingId, requestId, status } = row.original;

    // When listing is available, but no request were made yet or request is confirmed, show Cancel button
    if (requestId === null || status === SHIFT_EXCHANGE_REQUEST_STATUS.confirmed) {
        const isCancelling = submittingIds.has(getListingKey(listingId));
        return (
            <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => onCancelListing(listingId, status)}
                disabled={isCancelling}
            >
                {isCancelling && <i className="fas fa-spinner fa-spin mr-1" />}
                {translate("common.cancel", "Cancel")}
            </button>
        );
    }

    // When request is pending, show Confirm/Reject buttons
    if (status === SHIFT_EXCHANGE_REQUEST_STATUS.pending) {
        const isConfirming = submittingIds.has(getRequestConfirmKey(requestId));
        const isRejecting = submittingIds.has(getRequestRejectKey(requestId));
        const isDisabled = isConfirming || isRejecting;
        return (
            <div className="btn-group">
                <button
                    className="btn btn-sm btn-success mr-1"
                    onClick={() => onConfirmRequest(requestId!)}
                    disabled={isDisabled}
                >
                    {isConfirming && <i className="fas fa-spinner fa-spin mr-1" />}
                    {translate("common.confirm", "Confirm")}
                </button>
                <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => onRejectRequest(requestId!)}
                    disabled={isDisabled}
                >
                    {isRejecting && <i className="fas fa-spinner fa-spin mr-1" />}
                    {translate("common.reject", "Reject")}
                </button>
            </div>
        );
    }

    return null;
};

type WorkerExchangeMyOfferingsProps = {
    className?: string;
    myListings: IShiftExchangeListing[];
    incomingRequests: IShiftExchangeRequest[];
    people: IPersonData[];
    competencesById: Map<number, ICompetenceData>;
    lineGroupId: number;
    lineGroups: ILineGroupData[];
    isRefetching: boolean;
    onError: (error: string) => void;
    onOfferCanceled: () => Promise<void>;
    onRequestConfirmed: () => Promise<void>;
    onRequestRejected: () => Promise<void>;
    onAddNewOffer: () => void;
    selectedPersonId: number | null;
};

const WorkerExchangeMyOfferings = (props: WorkerExchangeMyOfferingsProps) => {
    const {
        className,
        myListings,
        incomingRequests,
        people,
        selectedPersonId,
        competencesById,
        lineGroupId,
        lineGroups,
        isRefetching,
        onError,
        onOfferCanceled,
        onRequestConfirmed,
        onRequestRejected,
        onAddNewOffer,
    } = props;

    const [submittingIds, setSubmittingIds] = useState<Set<string>>(new Set());

    // Transform listings and requests into table rows
    const tableData: MyOfferingsRow[] = useMemo(
        () => buildMyOfferingsRows(myListings, incomingRequests, people, competencesById, lineGroups),
        [myListings, incomingRequests, people, competencesById, lineGroups]
    );

    const uniqueShiftValues = useMemo(() => getUniqueShiftValues(tableData), [tableData]);
    const uniqueCompetenceCodes = useMemo(() => getUniqueCompetenceCodes(tableData), [tableData]);
    const uniquePersonNames = useMemo(() => getUniqueValues(tableData, "personName"), [tableData]);
    const uniqueStatuses = useMemo(() => getUniqueValues(tableData, "status"), [tableData]);
    const uniqueDestinationLines = useMemo(() => getUniqueValues(tableData, "destinationLineGroupTitle"), [tableData]);

    // Find the person name from selectedPersonId for initial filter
    const initialPersonName = useMemo(() => {
        if (selectedPersonId === null) {
            return null;
        }
        const selectedPerson = people.find(person => person.id === selectedPersonId);
        return selectedPerson?.name ?? null;
    }, [selectedPersonId, people]);

    const handleCancelListing = async (listingId: number, status: ExchangeListingStatus) => {
        const key = getListingKey(listingId);
        setSubmittingIds(prev => new Set(prev).add(key));
        onError("");

        try {
            if (status === SHIFT_EXCHANGE_LISTING_STATUS.confirmed) {
                await cancelExchangeListing(lineGroupId, listingId);
            } else {
                await deleteExchangeListing(lineGroupId, listingId);
            }

            await onOfferCanceled();
        } catch (err: unknown) {
            onError(translate("WorkerExchange.failedToCancelOffer", "Failed to cancel the offer"));
            console.error("Failed to cancel offer:", err);
        } finally {
            setSubmittingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(key);
                return newSet;
            });
        }
    };

    const handleConfirmRequest = async (requestId: number) => {
        const key = getRequestConfirmKey(requestId);
        setSubmittingIds(prev => new Set(prev).add(key));
        onError("");

        try {
            await acceptExchangeRequest(lineGroupId, requestId);
            await onRequestConfirmed();
        } catch (err: unknown) {
            onError(translate("WorkerExchange.failedToConfirmRequest", "Failed to confirm the request"));
            console.error("Failed to confirm request:", err);
        } finally {
            setSubmittingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(key);
                return newSet;
            });
        }
    };

    const handleRejectRequest = async (requestId: number) => {
        const key = getRequestRejectKey(requestId);
        setSubmittingIds(prev => new Set(prev).add(key));
        onError("");

        try {
            await rejectExchangeRequest(lineGroupId, requestId);
            await onRequestRejected();
        } catch (err: unknown) {
            onError(translate("WorkerExchange.failedToRejectRequest", "Failed to reject the request"));
            console.error("Failed to reject request:", err);
        } finally {
            setSubmittingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(key);
                return newSet;
            });
        }
    };

    const columns: ReactTableColumn<MyOfferingsRow>[] = [
        {
            Header: "",
            accessor: "status",
            Cell: StatusCell,
            enableSorting: true,
            sortType: sortString,
            filter: filterMultiSelect,
            Filter: (props: Column<MyOfferingsRow>) => (
                <MultiSelectFilter
                    values={uniqueStatuses}
                    id={props.column.id}
                    setFilter={props.setFilter}
                    filterValue={props.column.filterValue}
                    placeholder={translate("common.status", "Status")}
                    getLabel={getStatusLabel}
                />
            ),
        },
        {
            Header: "",
            accessor: "personName",
            enableSorting: true,
            sortType: sortString,
            filter: filterMultiSelect,
            Filter: (props: Column<AvailabilityRow>) => (
                <MultiSelectFilter
                    values={uniquePersonNames}
                    id={props.column.id}
                    setFilter={props.setFilter}
                    filterValue={props.column.filterValue}
                    placeholder={translate("common.people", "People")}
                />
            ),
        },
        {
            Header: "",
            accessor: "destinationLineGroupTitle",
            Cell: DestinationCell,
            enableSorting: true,
            sortType: sortString,
            filter: filterMultiSelect,
            Filter: (props: Column<MyOfferingsRow>) => (
                <MultiSelectFilter
                    values={uniqueDestinationLines}
                    id={props.column.id}
                    setFilter={props.setFilter}
                    filterValue={props.column.filterValue}
                    placeholder={translate("common.destination", "Destination")}
                />
            ),
        },
        {
            Header: "",
            accessor: "competences",
            Cell: CompetencesCell,
            enableSorting: true,
            sortType: sortCompetences,
            filter: filterCompetencesMultiSelect,
            Filter: (props: Column<AvailabilityRow>) => (
                <MultiSelectFilter
                    values={uniqueCompetenceCodes}
                    id={props.column.id}
                    setFilter={props.setFilter}
                    filterValue={props.column.filterValue}
                    placeholder={translate("common.competences", "Competences")}
                />
            ),
        },
        {
            Header: "",
            accessor: "date",
            Cell: DateCell,
            textAlign: "right",
            filter_type: "date",
            filter: filterEqualDay,
            filterInputPlaceholder: translate("common.date", "Date"),
            DateFilter: ShiftDateFilter,
            enableSorting: true,
            sortType: sortNumber,
        },
        {
            Header: "",
            accessor: "shiftNumber",
            Cell: ShiftCell,
            textAlign: "center",
            enableSorting: true,
            sortType: sortNumber,
            filter: filterShiftMultiSelect,
            Filter: (props: Column<AvailabilityRow>) => (
                <MultiSelectFilter
                    values={uniqueShiftValues}
                    id={props.column.id}
                    setFilter={props.setFilter}
                    filterValue={props.column.filterValue}
                    placeholder={translate("common.shifts", "Shifts")}
                />
            ),
        },
        {
            Header: translate("common.actions", "Actions"),
            accessor: "listingId",
            disableFilters: true,
            Cell: ({ row }) => (
                <MyOfferingsActionsCell
                    row={row}
                    submittingIds={submittingIds}
                    onCancelListing={handleCancelListing}
                    onConfirmRequest={handleConfirmRequest}
                    onRejectRequest={handleRejectRequest}
                />
            ),
            textAlign: "right",
        },
    ];

    // Build initialState for ReactTable to preselect the person filter
    const tableInitialState = useMemo(() => {
        if (initialPersonName === null) {
            return undefined;
        }

        return {
            filters: [
                {
                    id: "personName",
                    value: [initialPersonName],
                },
            ],
        };
    }, [initialPersonName]);

    const filteredTableData = useMemo(
        () => (selectedPersonId ? tableData.filter(row => row.personId === selectedPersonId) : []),
        [selectedPersonId, tableData]
    );

    const filteredColumns = useMemo(() => {
        return selectedPersonId
            ? columns.filter(column => column.accessor !== "personName" && column.accessor !== "competences")
            : columns;
    }, [selectedPersonId, columns]);

    const errorMessage =
        selectedPersonId && filteredTableData.length === 0
            ? translate("WorkerExchange.warning.noOfferingsForPerson", "No offerings remain for the selected person")
            : undefined;

    return (
        <>
            <WorkerExchangeSubHeader
                variant="offerings"
                title={
                    <>
                        {initialPersonName ? (
                            <i className="fas fa-user icon-smaller" />
                        ) : (
                            <i className="fas fa-list icon-smaller" />
                        )}
                        {translate("WorkerExchange.activeOfferings", "Active offerings")}
                        <strong>{initialPersonName ? `(${initialPersonName})` : ""}</strong>
                        {isRefetching && <i className="fas fa-spinner fa-spin" />}
                    </>
                }
                menu={
                    <div className="d-flex gap-2">
                        <button className="btn btn-primary btn-outline-primary" onClick={onAddNewOffer}>
                            <i className="fas fa-plus mr-1" />
                            {translate("WorkerExchange.addNewOfferings", "Add new offerings")}
                        </button>
                    </div>
                }
            />
            <div className={classNames("worker-exchange-my-offerings", className)}>
                <ReactTable
                    data={selectedPersonId ? filteredTableData : tableData}
                    columns={selectedPersonId ? filteredColumns : columns}
                    initialState={tableInitialState}
                    error_msg={errorMessage}
                />
            </div>
        </>
    );
};

// ---------------------------------------------------------------------------------------------------------------------
// WorkerExchangeOtherOfferings
// ---------------------------------------------------------------------------------------------------------------------

type OtherOfferingsActionsCellProps = {
    row: { original: OtherOfferingsRow };
    submittingIds: Set<number>;
    onRequest: (listingId: number) => void;
};

const OtherOfferingsActionsCell = (props: OtherOfferingsActionsCellProps) => {
    const { row, submittingIds, onRequest } = props;
    const { listingId } = row.original;
    const isSubmitting = submittingIds.has(listingId);

    return (
        <button className="btn btn-sm btn-primary" onClick={() => onRequest(listingId)} disabled={isSubmitting}>
            {isSubmitting && <i className="fas fa-spinner fa-spin mr-1" />}
            {translate("common.request", "Request")}
        </button>
    );
};

type WorkerExchangeOtherOfferingsProps = {
    className?: string;
    otherListings: IShiftExchangeListing[];
    outgoingRequests: IShiftExchangeRequest[];
    people: IPersonData[];
    competencesById: Map<number, ICompetenceData>;
    lineGroupId: number;
    lineGroups: ILineGroupData[];
    isRefetching: boolean;
    onError: (error: string) => void;
    onRequestCreated: () => Promise<void>;
    onViewBookings: () => void;
};

const WorkerExchangeOtherOfferings = (props: WorkerExchangeOtherOfferingsProps) => {
    const {
        className,
        otherListings,
        outgoingRequests,
        people,
        competencesById,
        lineGroupId,
        lineGroups,
        isRefetching,
        onError,
        onRequestCreated,
        onViewBookings,
    } = props;

    const [submittingListingIds, setSubmittingListingIds] = useState<Set<number>>(new Set());

    // Transform to table rows
    const tableData: OtherOfferingsRow[] = useMemo(
        () => buildOtherOfferingsRows(otherListings, outgoingRequests, people, competencesById, lineGroups),
        [otherListings, outgoingRequests, people, competencesById, lineGroups]
    );

    const uniqueShiftValues = useMemo(() => getUniqueShiftValues(tableData), [tableData]);
    const uniqueCompetenceCodes = useMemo(() => getUniqueCompetenceCodes(tableData), [tableData]);
    const uniquePersonNames = useMemo(() => getUniqueValues(tableData, "personName"), [tableData]);
    const uniqueSourceLineGroupTitles = useMemo(() => getUniqueValues(tableData, "sourceLineGroupTitle"), [tableData]);

    const handleRequest = async (listingId: number) => {
        setSubmittingListingIds(prev => new Set(prev).add(listingId));
        onError("");

        try {
            await createExchangeRequest(lineGroupId, listingId);
            await onRequestCreated();
        } catch (err: unknown) {
            onError(translate("WorkerExchange.failedToCreateRequest", "Failed to create the request"));
            console.error("Failed to create request:", err);
        } finally {
            setSubmittingListingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(listingId);
                return newSet;
            });
        }
    };

    const columns: ReactTableColumn<OtherOfferingsRow>[] = [
        {
            Header: "",
            accessor: "personName",
            enableSorting: true,
            sortType: sortString,
            filter: filterMultiSelect,
            Filter: (props: Column<AvailabilityRow>) => (
                <MultiSelectFilter
                    values={uniquePersonNames}
                    id={props.column.id}
                    setFilter={props.setFilter}
                    filterValue={props.column.filterValue}
                    placeholder={translate("common.people", "People")}
                />
            ),
        },
        {
            Header: "",
            accessor: "sourceLineGroupTitle",
            Cell: SourceCell,
            enableSorting: true,
            sortType: sortString,
            filter: filterMultiSelect,
            Filter: (props: Column<OtherOfferingsRow>) => (
                <MultiSelectFilter
                    values={uniqueSourceLineGroupTitles}
                    id={props.column.id}
                    setFilter={props.setFilter}
                    filterValue={props.column.filterValue}
                    placeholder={translate("common.source", "Source")}
                />
            ),
        },
        {
            Header: "",
            accessor: "competences",
            Cell: CompetencesCell,
            enableSorting: true,
            sortType: sortCompetences,
            filter: filterCompetencesMultiSelect,
            Filter: (props: Column<AvailabilityRow>) => (
                <MultiSelectFilter
                    values={uniqueCompetenceCodes}
                    id={props.column.id}
                    setFilter={props.setFilter}
                    filterValue={props.column.filterValue}
                    placeholder={translate("common.competences", "Competences")}
                />
            ),
        },
        {
            Header: "",
            accessor: "date",
            Cell: DateCell,
            textAlign: "right",
            filter_type: "date",
            filter: filterEqualDay,
            filterInputPlaceholder: translate("common.date", "Date"),
            DateFilter: ShiftDateFilter,
            enableSorting: true,
            sortType: sortNumber,
        },
        {
            Header: "",
            accessor: "shiftNumber",
            Cell: ShiftCell,
            textAlign: "center",
            enableSorting: true,
            sortType: sortNumber,
            filter: filterShiftMultiSelect,
            Filter: (props: Column<AvailabilityRow>) => (
                <MultiSelectFilter
                    values={uniqueShiftValues}
                    id={props.column.id}
                    setFilter={props.setFilter}
                    filterValue={props.column.filterValue}
                    placeholder={translate("common.shifts", "Shifts")}
                />
            ),
        },
        {
            Header: translate("common.actions", "Actions"),
            accessor: "listingId",
            disableFilters: true,
            Cell: ({ row }) => (
                <OtherOfferingsActionsCell row={row} submittingIds={submittingListingIds} onRequest={handleRequest} />
            ),
            textAlign: "right",
        },
    ];

    return (
        <>
            <WorkerExchangeSubHeader
                variant="bookings"
                title={
                    <>
                        <i className="fas fa-plus icon-smaller" />
                        {translate("WorkerExchange.addNewBookings", "Add new bookings")}
                        {isRefetching && <i className="fas fa-spinner fa-spin" />}
                    </>
                }
                menu={
                    <div className="d-flex gap-2">
                        <button className="btn btn-primary btn-outline-primary" onClick={onViewBookings}>
                            <i className="fas fa-list mr-1" />
                            {translate("WorkerExchange.activeBookings", "Active bookings")}
                        </button>
                    </div>
                }
            />
            <div className={classNames("worker-exchange-other-offerings", className)}>
                <ReactTable data={tableData} columns={columns} />
            </div>
        </>
    );
};

// ---------------------------------------------------------------------------------------------------------------------
// WorkerExchangeMyBookings
// ---------------------------------------------------------------------------------------------------------------------

type MyBookingsActionsCellProps = {
    row: { original: MyBookingsRow };
    submittingIds: Set<number>;
    onCancelRequest: (
        requestId: number,
        listingId: number,
        status: ShiftExchangeRequestStatusEnum,
        sourceLineGroupId: number
    ) => void;
};

const MyBookingsActionsCell = (props: MyBookingsActionsCellProps) => {
    const { row, submittingIds, onCancelRequest } = props;
    const { requestId, listingId, sourceLineGroupId, status } = row.original;
    const isSubmitting = submittingIds.has(requestId);

    // When request is pending or confirmed, show Cancel button
    if (status !== SHIFT_EXCHANGE_REQUEST_STATUS.rejected) {
        return (
            <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => onCancelRequest(requestId, listingId, status, sourceLineGroupId)}
                disabled={isSubmitting}
            >
                {isSubmitting && <i className="fas fa-spinner fa-spin mr-1" />}
                {translate("common.cancel", "Cancel")}
            </button>
        );
    }

    return null;
};

type WorkerExchangeMyBookingsProps = {
    className?: string;
    outgoingRequests: IShiftExchangeRequest[];
    otherListings: IShiftExchangeListing[];
    people: IPersonData[];
    competencesById: Map<number, ICompetenceData>;
    lineGroups: ILineGroupData[];
    lineGroupId: number;
    isRefetching: boolean;
    onError: (error: string) => void;
    onRequestCanceled: () => Promise<void>;
    onMakeNewRequest: () => void;
};

const WorkerExchangeMyBookings = (props: WorkerExchangeMyBookingsProps) => {
    const {
        className,
        outgoingRequests,
        otherListings,
        people,
        competencesById,
        lineGroups,
        lineGroupId,
        isRefetching,
        onError,
        onRequestCanceled,
        onMakeNewRequest,
    } = props;

    const [submittingRequestIds, setSubmittingRequestIds] = useState<Set<number>>(new Set());

    // Transform to table rows
    const tableData: MyBookingsRow[] = useMemo(
        () => buildMyBookingsRows(outgoingRequests, otherListings, people, competencesById, lineGroups),
        [outgoingRequests, otherListings, people, competencesById, lineGroups]
    );

    const uniqueShiftValues = useMemo(() => getUniqueShiftValues(tableData), [tableData]);
    const uniqueCompetenceCodes = useMemo(() => getUniqueCompetenceCodes(tableData), [tableData]);
    const uniquePersonNames = useMemo(() => getUniqueValues(tableData, "personName"), [tableData]);
    const uniqueStatuses = useMemo(() => getUniqueValues(tableData, "status"), [tableData]);
    const uniqueSourceLineGroupTitles = useMemo(() => getUniqueValues(tableData, "sourceLineGroupTitle"), [tableData]);

    const handleCancelRequest = async (
        requestId: number,
        listingId: number,
        status: ShiftExchangeRequestStatusEnum,
        sourceLineGroupId: number
    ) => {
        setSubmittingRequestIds(prev => new Set(prev).add(requestId));
        onError("");

        try {
            if (status === SHIFT_EXCHANGE_REQUEST_STATUS.pending) {
                await cancelExchangeRequest(lineGroupId, requestId);
            } else {
                await cancelExchangeListing(sourceLineGroupId, listingId);
            }

            await onRequestCanceled();
        } catch (err: unknown) {
            onError(translate("WorkerExchange.failedToCancelRequest", "Failed to cancel the request"));
            console.error("Failed to cancel request:", err);
        } finally {
            setSubmittingRequestIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(requestId);
                return newSet;
            });
        }
    };

    const columns: ReactTableColumn<MyBookingsRow>[] = [
        {
            Header: "",
            accessor: "status",
            Cell: StatusCell,
            enableSorting: true,
            sortType: sortString,
            filter: filterMultiSelect,
            Filter: (props: Column<MyOfferingsRow>) => (
                <MultiSelectFilter
                    values={uniqueStatuses}
                    id={props.column.id}
                    setFilter={props.setFilter}
                    filterValue={props.column.filterValue}
                    placeholder={translate("common.status", "Status")}
                    getLabel={getStatusLabel}
                />
            ),
        },
        {
            Header: "",
            accessor: "personName",
            Cell: NameAndIdCell,
            enableSorting: true,
            sortType: sortString,
            filter: filterMultiSelect,
            Filter: (props: Column<AvailabilityRow>) => (
                <MultiSelectFilter
                    values={uniquePersonNames}
                    id={props.column.id}
                    setFilter={props.setFilter}
                    filterValue={props.column.filterValue}
                    placeholder={translate("common.people", "People")}
                />
            ),
        },
        {
            Header: "",
            accessor: "sourceLineGroupTitle",
            Cell: SourceCell,
            enableSorting: true,
            sortType: sortString,
            filter: filterMultiSelect,
            Filter: (props: Column<MyBookingsRow>) => (
                <MultiSelectFilter
                    values={uniqueSourceLineGroupTitles}
                    id={props.column.id}
                    setFilter={props.setFilter}
                    filterValue={props.column.filterValue}
                    placeholder={translate("common.source", "Source")}
                />
            ),
        },
        {
            Header: "",
            accessor: "competences",
            Cell: CompetencesCell,
            enableSorting: true,
            sortType: sortCompetences,
            filter: filterCompetencesMultiSelect,
            Filter: (props: Column<AvailabilityRow>) => (
                <MultiSelectFilter
                    values={uniqueCompetenceCodes}
                    id={props.column.id}
                    setFilter={props.setFilter}
                    filterValue={props.column.filterValue}
                    placeholder={translate("common.competences", "Competences")}
                />
            ),
        },
        {
            Header: "",
            accessor: "date",
            Cell: DateCell,
            textAlign: "right",
            filter_type: "date",
            filter: filterEqualDay,
            filterInputPlaceholder: translate("common.date", "Date"),
            DateFilter: ShiftDateFilter,
            enableSorting: true,
            sortType: sortNumber,
        },
        {
            Header: "",
            accessor: "shiftNumber",
            Cell: ShiftCell,
            textAlign: "center",
            enableSorting: true,
            sortType: sortNumber,
            filter: filterShiftMultiSelect,
            Filter: (props: Column<AvailabilityRow>) => (
                <MultiSelectFilter
                    values={uniqueShiftValues}
                    id={props.column.id}
                    setFilter={props.setFilter}
                    filterValue={props.column.filterValue}
                    placeholder={translate("common.shifts", "Shifts")}
                />
            ),
        },
        {
            Header: translate("common.actions", "Actions"),
            accessor: "requestId",
            disableFilters: true,
            Cell: ({ row }) => (
                <MyBookingsActionsCell
                    row={row}
                    submittingIds={submittingRequestIds}
                    onCancelRequest={handleCancelRequest}
                />
            ),
            textAlign: "right",
        },
    ];

    return (
        <>
            <WorkerExchangeSubHeader
                variant="bookings"
                title={
                    <>
                        <i className="fas fa-list icon-smaller" />
                        {translate("WorkerExchange.activeBookings", "Active bookings")}
                        {isRefetching && <i className="fas fa-spinner fa-spin" />}
                    </>
                }
                menu={
                    <div className="d-flex gap-2">
                        <button className="btn btn-primary btn-outline-primary" onClick={onMakeNewRequest}>
                            <i className="fas fa-plus mr-1" />
                            {translate("WorkerExchange.addNewBookings", "Add new bookings")}
                        </button>
                    </div>
                }
            />
            <div className={classNames("worker-exchange-my-bookings", className)}>
                <ReactTable data={tableData} columns={columns} />
            </div>
        </>
    );
};

// ---------------------------------------------------------------------------------------------------------------------
// WorkerExchange
// ---------------------------------------------------------------------------------------------------------------------

type WorkerExchangeSubHeaderProps = {
    title?: React.ReactNode;
    menu?: React.ReactNode;
    variant?: "offerings" | "bookings";
};

const WorkerExchangeSubHeader = ({ title, menu, variant }: WorkerExchangeSubHeaderProps) => {
    return (
        <div
            className={classNames("worker-exchange__subheader-container", {
                "worker-exchange__subheader-container--offerings": variant === "offerings",
                "worker-exchange__subheader-container--bookings": variant === "bookings",
            })}
        >
            <div className="worker-exchange__subheader">{title}</div>
            {menu}
        </div>
    );
};

type WorkerExchangeProps = {
    lineGroupUuid: string;
    people: IPersonData[];
    peopleSchedule: IShiftPersonLineRec[];
    poolLine: IShiftPersonLineRec;
    selectedPersonId: number | null;
    week: number;
    year: number;
};

const WorkerExchange = (props: WorkerExchangeProps) => {
    const { lineGroupUuid, people, peopleSchedule, poolLine, selectedPersonId } = props;

    const [error, setError] = useState("");

    // Start with MY_OFFERINGS as default (will be updated when availabilities are loaded)
    const [view, setView] = useState<WorkerExchangeView>(WORKER_EXCHANGE_VIEWS.MY_OFFERINGS);

    const {
        competences,
        myListings,
        otherListings,
        outgoingRequests,
        incomingRequests,
        lineGroups,
        loadingState,
        error: fetchError,
        refetchListings,
        refetchRequests,
    } = useWorkerExchangeData(lineGroupUuid);

    const { lineGroupId, lineGroupExternalId } = useMemo(() => {
        const lineGroupData = lineGroups.find(lineGroup => lineGroup.uuid === lineGroupUuid);
        return {
            lineGroupId: lineGroupData?.id ?? 0,
            lineGroupExternalId: lineGroupData?.external_id ?? "",
        };
    }, [lineGroupUuid, lineGroups]);

    const competencesById = useMemo(() => {
        return new Map<number, ICompetenceData>(competences.map(competence => [competence.id, competence]));
    }, [competences]);

    const listingLookup = useMemo(() => buildExchangeListingLookup(myListings), [myListings]);

    const availabilities = useMemo(
        () => getExchangeAvailabilities(lineGroupExternalId, people, poolLine, peopleSchedule, listingLookup),
        [lineGroupExternalId, people, poolLine, peopleSchedule, listingLookup]
    );

    useEffect(() => {
        setView(getDefaultView(selectedPersonId));
    }, [selectedPersonId]);

    const activeTab = useMemo(() => getTabFromView(view), [view]);

    // Navigation handlers
    const handleViewOfferings = () => {
        setView(WORKER_EXCHANGE_VIEWS.MY_OFFERINGS);
    };

    const handleAddNewOffer = () => {
        setView(WORKER_EXCHANGE_VIEWS.MY_AVAILABILITIES);
    };

    const handleViewBookings = () => {
        setView(WORKER_EXCHANGE_VIEWS.MY_BOOKINGS);
    };

    const handleMakeNewRequest = () => {
        setView(WORKER_EXCHANGE_VIEWS.OTHER_OFFERINGS);
    };

    const handleTabChange = (tab: WorkerExchangeTab) => {
        if (tab === WORKER_EXCHANGE_TABS.OFFERINGS) {
            setView(WORKER_EXCHANGE_VIEWS.MY_OFFERINGS);
        } else {
            setView(WORKER_EXCHANGE_VIEWS.MY_BOOKINGS);
        }
    };

    // Show full loader only on initial load
    if (loadingState.all) {
        return (
            <div className="worker-exchange">
                <Loader />
            </div>
        );
    }

    const isRefetching = !loadingState.all && (loadingState.listings || loadingState.requests);
    const errorMessage = fetchError || error;

    const renderMyAvailabilities = () => (
        <WorkerExchangeMyAvailabilities
            className="worker-exchange__table"
            availabilities={availabilities}
            competencesById={competencesById}
            people={people}
            selectedPersonId={selectedPersonId}
            lineGroupId={lineGroupId}
            isRefetching={isRefetching}
            onError={setError}
            onOfferAdded={refetchListings}
            onViewOfferings={handleViewOfferings}
        />
    );

    const renderMyOfferings = () => (
        <WorkerExchangeMyOfferings
            className="worker-exchange__table"
            myListings={myListings}
            incomingRequests={incomingRequests}
            people={people}
            selectedPersonId={selectedPersonId}
            competencesById={competencesById}
            lineGroupId={lineGroupId}
            lineGroups={lineGroups}
            isRefetching={isRefetching}
            onError={setError}
            onOfferCanceled={async () => {
                await Promise.all([refetchListings(), refetchRequests()]);
            }}
            onRequestConfirmed={async () => {
                await Promise.all([refetchListings(), refetchRequests()]);
            }}
            onRequestRejected={async () => {
                await Promise.all([refetchListings(), refetchRequests()]);
            }}
            onAddNewOffer={handleAddNewOffer}
        />
    );

    const renderContent = () => {
        if (selectedPersonId) {
            return (
                <>
                    {view === WORKER_EXCHANGE_VIEWS.MY_AVAILABILITIES && renderMyAvailabilities()}
                    {view === WORKER_EXCHANGE_VIEWS.MY_OFFERINGS && renderMyOfferings()}
                </>
            );
        }
        return (
            <Tabs
                activeKey={activeTab}
                onSelect={eventKey => handleTabChange(eventKey as WorkerExchangeTab)}
                id="worker-exchange-tabs"
                className="worker-exchange__tabs"
                transition={false}
            >
                <Tab
                    eventKey={WORKER_EXCHANGE_TABS.OFFERINGS}
                    title={translate("WorkerExchange.myOfferings", "My Offerings")}
                >
                    {view === WORKER_EXCHANGE_VIEWS.MY_AVAILABILITIES && renderMyAvailabilities()}
                    {view === WORKER_EXCHANGE_VIEWS.MY_OFFERINGS && renderMyOfferings()}
                </Tab>
                <Tab
                    eventKey={WORKER_EXCHANGE_TABS.BOOKINGS}
                    title={translate("WorkerExchange.myBookings", "My Bookings")}
                >
                    {view === WORKER_EXCHANGE_VIEWS.OTHER_OFFERINGS && (
                        <WorkerExchangeOtherOfferings
                            className="worker-exchange__table"
                            otherListings={otherListings}
                            outgoingRequests={outgoingRequests}
                            people={people}
                            competencesById={competencesById}
                            lineGroupId={lineGroupId}
                            lineGroups={lineGroups}
                            isRefetching={isRefetching}
                            onError={setError}
                            onRequestCreated={refetchRequests}
                            onViewBookings={handleViewBookings}
                        />
                    )}
                    {view === WORKER_EXCHANGE_VIEWS.MY_BOOKINGS && (
                        <WorkerExchangeMyBookings
                            className="worker-exchange__table"
                            outgoingRequests={outgoingRequests}
                            otherListings={otherListings}
                            people={people}
                            competencesById={competencesById}
                            lineGroups={lineGroups}
                            lineGroupId={lineGroupId}
                            isRefetching={isRefetching}
                            onError={setError}
                            onRequestCanceled={refetchRequests}
                            onMakeNewRequest={handleMakeNewRequest}
                        />
                    )}
                </Tab>
            </Tabs>
        );
    };

    return (
        <div className="worker-exchange">
            {errorMessage && <ErrorComponent className="worker-exchange__error" msg={errorMessage} type="error" />}
            {renderContent()}
        </div>
    );
};

export default WorkerExchange;

export { WorkerExchangeModal };

export type { WorkerExchangeProps };
